import isEmpty from 'lodash/isEmpty';
import { isEqual } from 'lodash';

import NewsCard from '../../components/molecules/news-card/news-card';
import { archiveDates } from '../../constants/news';
import { PaginationModel } from '../../interface/pagination';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../../components/atoms/pagination/pagination';
import { useNewsAndEventsFilter } from '../../hooks/useNewsAndEventsFilter';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import { BannerWithCarouselModel } from '../../types/banner-with-carousel';
import ArchiveDates from '../../components/molecules/archive-dates/archive-dates';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import {
  getNewsAndEventsContent,
  getNewsBannerContent,
  getNewsCarouselContent
} from '../../services/news';
import Tabs from '../../components/molecules/tabs/tabs';
import HeaderContainer from '../../components/molecules/header-container/header-container';
import { newsRoutes } from '../../config/routes/pages';
import { ContentType } from '../../types/content';
import { ImageCarousel } from '../../components/molecules/image-carousel/image-carousel';
import {
  API_ASSETS_ROUTE,
  strapiAssetBaseUrl
} from '../../config/routes/assets';

type IndexProps = {
  newsBannerContent: ContentType;
  newsCarouselContent: BannerWithCarouselModel[];
  newsAndEventsContent: NewsAndEventsModel[];
  pagination: PaginationModel;
};

export function Index(props: IndexProps) {
  const {
    newsBannerContent,
    newsCarouselContent,
    newsAndEventsContent,
    pagination
  } = props;

  const { filteredNewsAndEventsContent, selectedYear, handleClickYear } =
    useNewsAndEventsFilter(newsAndEventsContent, '');

  const { currentContent, currentPage, handlePageClick } = usePagination(
    '',
    filteredNewsAndEventsContent
  );

  const newsCards = currentContent.map((newsItem) => {
    return (
      <NewsCard
        ctaLink={newsItem.attributes.slug}
        description={newsItem.attributes.news_excerpt}
        key={newsItem.attributes.slug}
        src={newsItem.attributes.news_image.data.attributes.url}
        title={newsItem.attributes.news_title}
      />
    );
  });

  const localNewsItems = [
    {
      year_posted: '2026',
      component: (
        <NewsCard
          ctaLink="090126"
          description="A dream kitchen begins with a vision and the right appliances bring that vision to life."
          key="090126"
          src={`${strapiAssetBaseUrl}/rebate_banner_resized_bfeda0548b.jpg`}
          title="Celebrating 80 Years of Innovation. Made for Your Dream Kitchen."
        />
      )
    },
    {
      year_posted: '2025',
      component: (
        <NewsCard
          ctaLink="103025"
          description="Sub-Zero and Wolf joined as an Event Partner at the MICHELIN Guide Ceremony Manila and Environs | Cebu 2026"
          key="103025"
          src={`${strapiAssetBaseUrl}/4_800ee74094.jpg`}
          title="Celebrating the Stars of the Kitchen"
        />
      )
    },
    {
      year_posted: '2025',
      component: (
        <NewsCard
          ctaLink="052625"
          description="Inside the Sub-Zero & Wolf x Antonio’s Pop-Up Experience"
          key="052625"
          src={`${strapiAssetBaseUrl}/1_e7ad7d6adb.jpg`}
          title="An Evening of Culinary Precision"
        />
      )
    },
    {
      year_posted: '2025',
      component: (
        <NewsCard
          ctaLink="032225"
          description=" A celebration of Thai cuisine where bold flavors and simple ingredients came together, powered by Sub-Zero and Wolf."
          key="032225"
          src={`${strapiAssetBaseUrl}/1_88c46f245b.png`}
          title="Taste of Thai"
        />
      )
    },
    {
      year_posted: '2024',
      component: (
        <NewsCard
          ctaLink="100524"
          description="Experience the vibrant flavors of fresh ingredients and see how satisfying good food can truly be as Sub-Zero and Wolf partner with Good Food Community."
          key="100524"
          src={`${strapiAssetBaseUrl}/1_b279b45bad.png`}
          title="A Fresh Take"
        />
      )
    },
    {
      year_posted: '2024',
      component: (
        <NewsCard
          ctaLink="042724"
          description="Delight in a symphony of soulful flavors that pay homage to classic Filipino recipes reimagined with a contemporary touch. Join our feast as we transform each dish into a celebration with the outstanding performance of Sub-Zero and Wolf appliances."
          key="042724"
          src={`${strapiAssetBaseUrl}/1_2bc96ffe58.jpg`}
          title="Rediscovering Filipino Classics"
        />
      )
    },
    {
      year_posted: '2023',
      component: (
        <NewsCard
          ctaLink="21524"
          description="Create memories worth celebrating with a Thanksgiving spread that
            harmoniously blends your favorite holiday flavors, incorporating the
            exceptional quality of Sub-Zero and Wolf bringing a feast in every bite."
          key="21524"
          src={`${strapiAssetBaseUrl}/1_4f53aaf4f1.png`}
          title="Grateful Gatherings"
        />
      )
    },
    {
      year_posted: '2023',
      component: (
        <NewsCard
          ctaLink="072923"
          description="Embark on a culinary adventure made with fresh ingredients as Sub-Zero and Wolf partners with Good Food Community for a 'Freshness Medley'"
          key="072923"
          src={`${strapiAssetBaseUrl}/1_85f6a5925e.jpg`}
          title="Indulge in a Fresh Perspective"
        />
      )
    },
    {
      year_posted: '2023',
      component: (
        <NewsCard
          ctaLink="26801"
          description="Experience the best of luxe summer living with Sub-Zero and Wolf's first cooking demo for the year."
          key="26801"
          src={`${strapiAssetBaseUrl}/1_8be5194c8b.jpg`}
          title="Summer in the Hamptons"
        />
      )
    }
  ];

  const filteredNewsCards = !isEmpty(selectedYear)
    ? [
        ...filteredNewsAndEventsContent
          .filter((newsItem) =>
            isEqual(newsItem.attributes.year_posted, selectedYear)
          )
          .map((newsItem) => {
            return (
              <NewsCard
                ctaLink={newsItem.attributes.slug}
                description={newsItem.attributes.news_excerpt}
                key={newsItem.attributes.slug}
                src={newsItem.attributes.news_image.data.attributes.url}
                title={newsItem.attributes.news_title}
              />
            );
          }),
        ...localNewsItems
          .filter((localItem) => isEqual(localItem.year_posted, selectedYear))
          .map((localItem) => localItem.component)
      ]
    : [...localNewsItems.map((localItem) => localItem.component), ...newsCards];

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <HeaderContainer {...newsBannerContent} />
      <Tabs tabItems={newsRoutes} />
      <div className="w-full max-w-defaultMax m-auto mt-10 lg:mt-20 lg:px-5 xl:px-0">
        <ImageCarousel carouselData={newsCarouselContent} />
      </div>
      <div className="px-10 mt-20">
        <ContainerWithSidebar
          featuredContents={
            <ArchiveDates
              dates={archiveDates}
              selectedYear={selectedYear}
              onClickSelectYear={handleClickYear}
            />
          }
        >
          <div className="space-y-8 mb-5 lg:mb-14">{filteredNewsCards}</div>
          <Pagination
            content={filteredNewsAndEventsContent}
            currentPage={currentPage}
            pageCount={pagination.pageCount}
            pageLimit={pagination.pageSize}
            onPageClick={handlePageClick}
          />
        </ContainerWithSidebar>
      </div>
    </>
  );
}

export async function getStaticProps() {
  const newsBannerContent = await getNewsBannerContent();
  const newsCarouselContent = await getNewsCarouselContent();
  const newsAndEventsContent = await getNewsAndEventsContent();

  const { pagination } = newsAndEventsContent.meta;

  return {
    props: {
      newsBannerContent,
      newsCarouselContent,
      newsAndEventsContent: newsAndEventsContent.data,
      pagination
    }
  };
}

export default Index;
